import { GetterTree, MutationTree, ActionTree } from 'vuex'

import { generateImagePath, gernerateOrderId } from '@/utils'
import { retryRequest } from '@/utils/request'
import * as Event from '@/utils/sendEvent'
import { uploadFileToServer } from '@/api/checkout'
import { IUploadImageServerRequest } from '@/interfaces/checkout'
import {
  IState,
  IUpdatePayload,
  IFigurePaintingOrderRequest,
  ImageInfo
} from '@/interfaces/figurePainting'
import {
  Style,
  Color,
  Body,
  Frame,
  FrameSize,
  FramePricing,
  CategoryPricing,
  BodyOptions,
  FrameOptions,
  FrameSizeOptions,
  IMAGE_URL
} from '@/constants/figurePainting'
import ImageUtil from '@/utils/image-util'

const getDefaultState = (): IState => ({
  form: {
    style: Style.STORY,
    color: Color.PINK,
    body: Body.BUST,
    frame: Frame.VELVET,
    frameSize: FrameSize['20x20'],
    numberOfPeople: 1,
    numberOfPaint: 1,
    text: '',
    agreeToShare: true
  },
  image: null,
  imageInfo: {},
  uploading: false,
  createing: false
})

const state = getDefaultState()

const actions: ActionTree<IState, any> = {
  handleFormUpdate ({ commit }, payload) {
    return commit('UPDATE_FORM', payload)
  },
  async handleImageUpdate ({ commit, state }, payload: File) {
    try {
      commit('UPDATE_UPLOADING', true)
      const { imageInfo: { checkBrowser } } = state
      if (!payload) { return commit('UPDATE_IMAGE', null) }

      const image = await ImageUtil.getFileImageByByte(payload)
      if (!['image/jpeg', 'image/png'].includes(image.type)) {
        throw new Error('請選擇 .jpg或.png 圖片')
      }
      if (!checkBrowser) {
        const { orientation } = await ImageUtil.getExif(image)
        commit('UPDATE_IMAGE_INFO', { orientation })
      }
      return commit('UPDATE_IMAGE', image)
    } catch (error) {
      console.log(error)
      commit('UPDATE_UPLOADING', false)
      return Promise.reject(error)
    }
  },
  async handleImageUpload ({ commit, rootGetters, state }) {
    const { image } = state
    const {
      webAppCaps,
      token,
      'webview/platform': prefix
    } = rootGetters
    const { origin: serverUrl } = new URL(webAppCaps.upload_image_server)
    const path = generateImagePath(prefix)
    commit('UPDATE_UPLOADING', true)
    try {
      if (image) {
        const fileExtension = image.type.split('/').pop()
        // jpeg => jpg @TODO optimize
        const filename = `000.${fileExtension?.replace('e', '')}`
        const { data: [result] } = await uploadFileToServer(
          {
            token,
            app_index: 6,
            login: 'illuxtech',
            destDir: `ToPrint\\\\${path}`,
            pwd: '1234',
            file: image
          } as IUploadImageServerRequest,
          filename,
          serverUrl
        )
        if (result.flag === 1) { throw new Error(`flag: ${result.flag}`) }
        return commit('UPDATE_IMAGE_INFO', { filename, path })
      }
    } catch (error) {
      console.log(error)
      commit('UPDATE_IMAGE', null)
    }
    commit('UPDATE_UPLOADING', false)
    return Promise.reject(new Error('圖片上傳失敗，請確認您的網路狀態是否正常，並重新選擇圖片'))
  },
  async createOrder ({ commit, state, rootGetters }) {
    const {
      token,
      'webview/ver_no': verNo,
      'webview/device': device,
      'webview/os_ver': osVer,
      'webview/bank_name': bankName,
      'webview/cal1_cost': cal1Cost,
      webAppCaps
    } = rootGetters
    const { imageInfo, form } = state
    const orderId = gernerateOrderId()

    if (!imageInfo.filename && !imageInfo.path) {
      return Promise.reject(new Error())
    }

    const requestBody = {
      token,
      order_id: orderId,
      app_index: process.env.VUE_APP_INDEX,
      product: '120',
      product_num: 1,
      uname: '',
      utel: '',
      umail: '',
      rec_name: '',
      rec_address: '',
      rec_phone: '',
      rec_country: '',
      rec_company: '',
      tel_country: '',
      image_number: 1,
      image_filename: imageInfo.filename,
      image_path: imageInfo.path,
      duplicate_num: '1',
      image_ISO: '',
      model_name: '',
      original_width: '',
      original_height: '',
      date_time: '',
      style_id: '0',
      caption: '',
      tx_status: 13,
      tx_type: 0,
      print_datetime: 0,
      amount: 0,
      ver_no: verNo,
      device,
      os_ver: osVer,
      bank_id: '',
      item_number: '',
      onemore: '',
      style_ver: 14,
      merge_order_id: '',
      deliver_fee: '',
      amount_msg: '',
      mode: 0,
      receipt_no: '',
      cpid: '',
      coin_used: '',
      coin_add: '',
      prime_join: 0,
      prime_cnt: 0,
      prime_cnt_add: '+0',
      calendar1_stmonth: '',
      product_model: '',
      resp: webAppCaps.comp_resp_credit,
      bank_name: bankName,
      cal1_cost: cal1Cost,
      upload_image_server: webAppCaps.upload_image_server,
      product120_body: form.body,
      product120_color: form.color,
      product120_frame: form.frame,
      product120_frame_size: form.frameSize,
      product120_number_of_paint: form.numberOfPaint,
      product120_number_of_people: form.numberOfPeople,
      product120_style: form.style,
      product120_text: form.text,
      product120_share: +form.agreeToShare
    } as IFigurePaintingOrderRequest

    try {
      commit('UPDATE_CREATING', true)
      commit('logger/PUSH_LOG', { orderId }, { root: true })
      commit('logger/PUSH_LOG', { setOrderInfoRequest: requestBody }, { root: true })

      const { data: [result] } = await retryRequest({
        url: '/128_SetOrderInfoServer_v2_tok.php',
        method: 'post',
        timeout: 60000,
        data: requestBody
      })
      if (!result || result.flag === 1) {
        throw new Error('setFigurePaintingOrder fail')
      }
      console.log('setFigurePaintingOrder success', orderId)
      return commit('RESET_STATE')
    } catch (error) {
      commit('UPDATE_CREATING', false)
      commit('logger/PUSH_LOG', error, { root: true })
      return Promise.reject(new Error('加入購物車時發生錯誤，請檢查您的網路狀態是否不佳，若有問題請聯絡客服。'))
    }
  },
  async checkBrowser ({ commit }) {
    try {
      const checkBrowser = await ImageUtil.checkBrowser()
      return commit('UPDATE_IMAGE_INFO', { checkBrowser })
    } catch (error) {
      console.log(error)
      return commit('UPDATE_IMAGE_INFO', { checkBrowser: true })
    }
  },
  async createOrderToCart ({ dispatch }) {
    try {
      await dispatch('handleImageUpload')
      await dispatch('createOrder')
      Event.addToCart(120, '似顏無框畫', 1)
    } catch (error) {
      return Promise.reject(error)
    }
    return Promise.resolve()
  }
}

const mutations: MutationTree<IState> = {
  UPDATE_FORM (state: IState, payload: IUpdatePayload) {
    const { value, target } = payload
    state.form = {
      ...state.form,
      [target]: value
    }
    switch (target) {
      case 'numberOfPeople':
        if (value >= 3) {
          state.form.frameSize = FrameSize['30x20']
        }
        break
    }
  },
  UPDATE_IMAGE (state: IState, payload: File) {
    state.image = payload
    state.uploading = false
    state.imageInfo.filename = ''
    state.imageInfo.path = ''
  },
  UPDATE_IMAGE_INFO (state: IState, payload: ImageInfo) {
    state.imageInfo = { ...state.imageInfo, ...payload }
    state.uploading = false
  },
  UPDATE_UPLOADING (state: IState, payload: boolean) {
    state.uploading = payload
  },
  UPDATE_CREATING (state: IState, payload: boolean) {
    state.createing = payload
  },
  RESET_STATE (state: IState) {
    const newState = getDefaultState()
    const keys = Object.keys(newState) as Array<keyof IState>
    keys
      .forEach(key => {
        state[key] = newState[key] as any
      })
  }
}

const getters: GetterTree<IState, any> = {
  total (state: IState, getters: any): number {
    const { form: { numberOfPaint } } = state
    const { pricePerPeople, priceByFrame } = getters
    return pricePerPeople + priceByFrame * numberOfPaint
  },
  preview (state: IState): string {
    const { form: { style, color, body } } = state
    const imageFileName = [style, body, color].join('_')
    return `${IMAGE_URL}/images/${imageFileName}.jpeg`
  },
  painting ():string {
    return `${IMAGE_URL}/lottie_player/img_painting.svg`
  },
  hasFrame (state: IState): boolean {
    const { form: { frame } } = state
    return frame !== Frame.NONE
  },
  currentPricing (state: IState): number {
    const { form: { style, body } } = state
    return CategoryPricing[body][style]
  },
  priceByFrame (state: IState): number {
    const { form: { frame } } = state
    return FramePricing[frame]
  },
  pricePerPeople (state: IState, getters: any): number {
    const { form: { numberOfPeople } } = state
    const { currentPricing } = getters
    return numberOfPeople * currentPricing
  },
  bodyLabel (state: IState): string | undefined {
    const { form: { body } } = state
    const currentBody = BodyOptions.find(option => option.id === body)
    return currentBody?.label
  },
  frameLabel (state: IState): string | undefined {
    const { form: { frame } } = state
    const currentFrame = FrameOptions.find(option => option.id === frame)
    return currentFrame?.label
  },
  frameSizeLabel (state: IState): string | undefined {
    const { form: { frameSize } } = state
    const currentFrameSize = FrameSizeOptions
      .find(option => option.id === frameSize)
    return currentFrameSize?.label
  },
  previewRotation (state: IState) {
    const {
      imageInfo: {
        orientation = 1,
        checkBrowser = true
      }
    } = state
    const transformMap = {
      1: 'rotate(0deg)',
      2: 'rotate(0deg) scaleX(-1)',
      3: 'rotate(180deg)',
      4: 'rotate(180deg) scaleX(-1)',
      5: 'rotate(90deg) scale(1, -1)',
      6: 'rotate(90deg)',
      7: 'rotate(270deg) scale(1, -1)',
      8: 'rotate(270deg)'
    } as { [key in number]: string }
    return (!checkBrowser && transformMap[orientation]) || 'none'
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
